import request from "../../utils/request";

// Types
export interface ChannelPreference {
  enabled: boolean;
  source: "default" | "user" | "admin";
  locked: boolean;
}

export interface NotificationPreferenceView {
  notificationTypeId: number;
  notificationTypeName: string;
  email: ChannelPreference;
  push: ChannelPreference;
  inApp: ChannelPreference;
}

export interface CompanyNotificationSettingView {
  notificationTypeId: number;
  notificationTypeName: string;
  emailsDisabled: boolean;
  mobilePushNotificationsDisabled: boolean;
  inAppDisabled: boolean;
}

export interface NotificationPreferenceItemInput {
  notificationTypeId: number;
  emailEnabled: boolean | null;
  pushEnabled: boolean | null;
  inAppEnabled: boolean | null;
}

export interface UserNotificationSummaryView {
  userId: string;
  fullName: string;
  email: string | null;
  department: string | null;
  branch: string | null;
  role: string | null;
  customOverrideCount: number;
}

export interface UserNotificationSummaryListView {
  users: UserNotificationSummaryView[];
  totalCount: number;
}

interface Result<T> {
  payload: T;
  status: number;
  errors: Array<{ description: string }>;
}

// User preferences
export async function getUserPreferences() {
  const response = await request.get<Result<NotificationPreferenceView[]>>(
    "NotificationPreference/user"
  );
  return response.data;
}

export async function updateUserPreferences(
  preferences: NotificationPreferenceItemInput[]
) {
  const response = await request.put<Result<boolean>>(
    "NotificationPreference/user",
    { preferences }
  );
  return response.data;
}

// Admin: company settings
export async function getCompanyNotificationSettings() {
  const response = await request.get<Result<CompanyNotificationSettingView[]>>(
    "NotificationPreference/company"
  );
  return response.data;
}

export async function updateCompanyNotificationSettings(
  settings: {
    notificationTypeId: number;
    emailsDisabled: boolean;
    mobilePushNotificationsDisabled: boolean;
    inAppDisabled: boolean;
  }[]
) {
  const response = await request.put<Result<boolean>>(
    "NotificationPreference/company",
    { settings }
  );
  return response.data;
}

// Admin: user management
export async function getUsersWithPreferences(params: {
  departmentId?: number;
  branchId?: number;
  role?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}) {
  const response = await request.get<Result<UserNotificationSummaryListView>>(
    "NotificationPreference/users",
    { params }
  );
  return response.data;
}

export async function getUserPreferencesById(userId: string) {
  const response = await request.get<Result<NotificationPreferenceView[]>>(
    `NotificationPreference/users/${userId}`
  );
  return response.data;
}

export async function updateUserPreferencesById(
  userId: string,
  preferences: NotificationPreferenceItemInput[]
) {
  const response = await request.put<Result<boolean>>(
    `NotificationPreference/users/${userId}`,
    { preferences }
  );
  return response.data;
}

export async function bulkUpdatePreferences(input: {
  filter?: {
    departmentId?: number;
    branchId?: number;
    role?: string;
    search?: string;
  };
  preferences: NotificationPreferenceItemInput[];
}) {
  const response = await request.put<Result<boolean>>(
    "NotificationPreference/users/bulk-update",
    input
  );
  return response.data;
}
